import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useSupplierAuth } from '../../context/SupplierAuthContext';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Eye, EyeOff, ArrowLeft, ArrowRight, Truck, Check, Plus, X } from 'lucide-react';
import { getUserErrorMessage } from '../../utils/errorMessages';

const SPOOKER_LOGO = "https://spooker.io/wp-content/uploads/2022/12/logo.svg";

const serviceOptions = [
  'MFA Implementatie',
  'Penetration Testing',
  'Security Awareness',
  'ISO27001 Certificering',
  'NIS2 Compliance',
  'Security Audit',
  'SIEM/SOC',
  'Incident Response',
  'Cloud Security',
  'Data Protection'
];

const sectorOptions = [
  'Finance',
  'Healthcare',
  'Overheid',
  'Manufacturing',
  'Retail',
  'Technology',
  'Education',
  'Energy',
  'Transport',
  'Legal'
];

const regionOptions = [
  'Nederland',
  'België',
  'Duitsland',
  'Frankrijk',
  'Luxemburg',
  'UK',
  'Europa breed',
  'Wereldwijd'
];

const SupplierRegisterPage = () => {
  const { register } = useSupplierAuth();
  const navigate = useNavigate();
  
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    company_name: '',
    country: 'NL',
    kvk_number: '',
    vat_number: '',
    website: '',
    description: '',
    services: [],
    sectors: [],
    regions: [],
    contact_email: '',
    contact_phone: '',
    admin_name: '',
    admin_email: '',
    admin_password: '',
    confirm_password: ''
  });

  const [newService, setNewService] = useState('');
  const [newSector, setNewSector] = useState('');
  const [newRegion, setNewRegion] = useState('');

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const addTag = (field, value) => {
    if (value && !formData[field].includes(value)) {
      updateField(field, [...formData[field], value]);
    }
  };

  const removeTag = (field, value) => {
    updateField(field, formData[field].filter(v => v !== value));
  };

  const validateStep1 = () => {
    if (!formData.company_name || !formData.description || formData.services.length === 0) {
      toast.error('Vul alle verplichte velden in');
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    if (!formData.admin_name || !formData.admin_email || !formData.admin_password) {
      toast.error('Vul alle verplichte velden in');
      return false;
    }
    if (formData.admin_password !== formData.confirm_password) {
      toast.error('Wachtwoorden komen niet overeen');
      return false;
    }
    if (formData.admin_password.length < 8) {
      toast.error('Wachtwoord moet minimaal 8 tekens zijn');
      return false;
    }
    return true;
  };

  const handleNext = () => {
    if (step === 1 && validateStep1()) {
      setStep(2);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep2()) return;
    
    setLoading(true);
    try {
      const submitData = {
        company_name: formData.company_name,
        country: formData.country,
        kvk_number: formData.kvk_number || undefined,
        vat_number: formData.vat_number || undefined,
        website: formData.website || undefined,
        description: formData.description,
        services: formData.services,
        sectors: formData.sectors,
        regions: formData.regions,
        contact_email: formData.contact_email || formData.admin_email,
        contact_phone: formData.contact_phone || undefined,
        admin_name: formData.admin_name,
        admin_email: formData.admin_email,
        admin_password: formData.admin_password
      };
      
      await register(submitData);
      toast.success('Registratie succesvol! Uw aanvraag wordt beoordeeld.');
      navigate('/leverancier/login');
    } catch (error) {
      toast.error(getUserErrorMessage(error, 'Registratie mislukt'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-50 to-neutral-100 dark:from-neutral-950 dark:to-neutral-900 p-4 py-8">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <img src={SPOOKER_LOGO} alt="Spooker" className="h-8 mx-auto mb-4 dark:invert" />
          <div className="flex items-center justify-center gap-2 text-sm text-neutral-500 dark:text-neutral-400">
            <Truck className="h-4 w-4" />
            Leverancier Portal
          </div>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-4 mb-8">
          <div className={`flex items-center gap-2 ${step >= 1 ? 'text-black dark:text-white' : 'text-neutral-400'}`}>
            <div className={`h-8 w-8 rounded-full flex items-center justify-center ${step >= 1 ? 'bg-black dark:bg-white text-white dark:text-black' : 'bg-neutral-200 dark:bg-neutral-800'}`}>
              {step > 1 ? <Check className="h-4 w-4" /> : '1'}
            </div>
            <span className="text-sm font-medium">Bedrijfsgegevens</span>
          </div>
          <div className="w-12 h-0.5 bg-neutral-200 dark:bg-neutral-800" />
          <div className={`flex items-center gap-2 ${step >= 2 ? 'text-black dark:text-white' : 'text-neutral-400'}`}>
            <div className={`h-8 w-8 rounded-full flex items-center justify-center ${step >= 2 ? 'bg-black dark:bg-white text-white dark:text-black' : 'bg-neutral-200 dark:bg-neutral-800'}`}>
              2
            </div>
            <span className="text-sm font-medium">Account aanmaken</span>
          </div>
        </div>

        <Card>
          {step === 1 && (
            <>
              <CardHeader>
                <CardTitle>Bedrijfsgegevens</CardTitle>
                <CardDescription>
                  Vertel ons over uw bedrijf en expertise
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <Label htmlFor="company_name">Bedrijfsnaam *</Label>
                    <Input
                      id="company_name"
                      value={formData.company_name}
                      onChange={(e) => updateField('company_name', e.target.value)}
                      placeholder="Uw bedrijfsnaam"
                      required
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="country">Land *</Label>
                    <Select value={formData.country} onValueChange={(v) => updateField('country', v)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="NL">Nederland</SelectItem>
                        <SelectItem value="BE">België</SelectItem>
                        <SelectItem value="DE">Duitsland</SelectItem>
                        <SelectItem value="FR">Frankrijk</SelectItem>
                        <SelectItem value="LU">Luxemburg</SelectItem>
                        <SelectItem value="UK">Verenigd Koninkrijk</SelectItem>
                        <SelectItem value="OTHER">Anders</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="kvk_number">KvK Nummer</Label>
                    <Input
                      id="kvk_number"
                      value={formData.kvk_number}
                      onChange={(e) => updateField('kvk_number', e.target.value)}
                      placeholder="12345678"
                    />
                  </div>

                  <div>
                    <Label htmlFor="vat_number">BTW Nummer</Label>
                    <Input
                      id="vat_number"
                      value={formData.vat_number}
                      onChange={(e) => updateField('vat_number', e.target.value)}
                      placeholder="NL123456789B01"
                    />
                  </div>

                  <div>
                    <Label htmlFor="website">Website</Label>
                    <Input
                      id="website"
                      value={formData.website}
                      onChange={(e) => updateField('website', e.target.value)}
                      placeholder="https://uwbedrijf.nl"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="description">Beschrijving *</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => updateField('description', e.target.value)}
                    placeholder="Beschrijf uw diensten en expertise..."
                    rows={4}
                  />
                  <p className="text-xs text-neutral-500 mt-1">Minimaal 10 tekens</p>
                </div>

                {/* Services */}
                <div>
                  <Label>Diensten *</Label>
                  <div className="flex flex-wrap gap-2 mt-2 mb-3">
                    {formData.services.map((service) => (
                      <Badge key={service} variant="secondary" className="pl-2 pr-1 py-1">
                        {service}
                        <button onClick={() => removeTag('services', service)} className="ml-1 hover:text-red-500">
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <Select value={newService} onValueChange={(v) => { addTag('services', v); setNewService(''); }}>
                      <SelectTrigger className="flex-1">
                        <SelectValue placeholder="Selecteer een dienst" />
                      </SelectTrigger>
                      <SelectContent>
                        {serviceOptions.filter(s => !formData.services.includes(s)).map((service) => (
                          <SelectItem key={service} value={service}>{service}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Sectors */}
                <div>
                  <Label>Sectoren</Label>
                  <div className="flex flex-wrap gap-2 mt-2 mb-3">
                    {formData.sectors.map((sector) => (
                      <Badge key={sector} variant="secondary" className="pl-2 pr-1 py-1">
                        {sector}
                        <button onClick={() => removeTag('sectors', sector)} className="ml-1 hover:text-red-500">
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <Select value={newSector} onValueChange={(v) => { addTag('sectors', v); setNewSector(''); }}>
                      <SelectTrigger className="flex-1">
                        <SelectValue placeholder="Selecteer een sector" />
                      </SelectTrigger>
                      <SelectContent>
                        {sectorOptions.filter(s => !formData.sectors.includes(s)).map((sector) => (
                          <SelectItem key={sector} value={sector}>{sector}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Regions */}
                <div>
                  <Label>Regio's</Label>
                  <div className="flex flex-wrap gap-2 mt-2 mb-3">
                    {formData.regions.map((region) => (
                      <Badge key={region} variant="secondary" className="pl-2 pr-1 py-1">
                        {region}
                        <button onClick={() => removeTag('regions', region)} className="ml-1 hover:text-red-500">
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <Select value={newRegion} onValueChange={(v) => { addTag('regions', v); setNewRegion(''); }}>
                      <SelectTrigger className="flex-1">
                        <SelectValue placeholder="Selecteer een regio" />
                      </SelectTrigger>
                      <SelectContent>
                        {regionOptions.filter(r => !formData.regions.includes(r)).map((region) => (
                          <SelectItem key={region} value={region}>{region}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
              <CardFooter className="flex justify-between">
                <Link to="/leverancier/login">
                  <Button variant="ghost">
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Terug naar login
                  </Button>
                </Link>
                <Button onClick={handleNext}>
                  Volgende
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </CardFooter>
            </>
          )}

          {step === 2 && (
            <form onSubmit={handleSubmit}>
              <CardHeader>
                <CardTitle>Account aanmaken</CardTitle>
                <CardDescription>
                  Maak uw beheerdersaccount aan
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <Label htmlFor="admin_name">Uw naam *</Label>
                    <Input
                      id="admin_name"
                      value={formData.admin_name}
                      onChange={(e) => updateField('admin_name', e.target.value)}
                      placeholder="Uw volledige naam"
                      required
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="admin_email">E-mailadres *</Label>
                    <Input
                      id="admin_email"
                      type="email"
                      value={formData.admin_email}
                      onChange={(e) => updateField('admin_email', e.target.value)}
                      placeholder="u@bedrijf.nl"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="contact_phone">Telefoonnummer</Label>
                    <Input
                      id="contact_phone"
                      type="tel"
                      value={formData.contact_phone}
                      onChange={(e) => updateField('contact_phone', e.target.value)}
                      placeholder="+31 6 12345678"
                    />
                  </div>

                  <div>
                    <Label htmlFor="admin_password">Wachtwoord *</Label>
                    <div className="relative">
                      <Input
                        id="admin_password"
                        type={showPassword ? 'text' : 'password'}
                        value={formData.admin_password}
                        onChange={(e) => updateField('admin_password', e.target.value)}
                        placeholder="Minimaal 8 tekens"
                        required
                      />
                      <button
                        type="button"
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-500"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="confirm_password">Bevestig wachtwoord *</Label>
                    <Input
                      id="confirm_password"
                      type="password"
                      value={formData.confirm_password}
                      onChange={(e) => updateField('confirm_password', e.target.value)}
                      placeholder="Herhaal wachtwoord"
                      required
                    />
                  </div>
                </div>

                <div className="bg-neutral-50 dark:bg-neutral-800/50 rounded-lg p-4">
                  <p className="text-sm text-neutral-600 dark:text-neutral-400">
                    Door te registreren gaat u akkoord met onze{' '}
                    <a href="/terms" className="text-black dark:text-white underline">Leveranciersvoorwaarden</a>
                    {' '}en{' '}
                    <a href="/privacy" className="text-black dark:text-white underline">Privacybeleid</a>.
                  </p>
                </div>
              </CardContent>
              <CardFooter className="flex justify-between">
                <Button type="button" variant="ghost" onClick={() => setStep(1)}>
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Vorige
                </Button>
                <Button type="submit" disabled={loading}>
                  {loading ? 'Registreren...' : 'Registreren'}
                </Button>
              </CardFooter>
            </form>
          )}
        </Card>
      </div>
    </div>
  );
};

export default SupplierRegisterPage;
